import type { Locale } from '../text/locale.ts'
import { civilFromDays } from './civil.ts'
import type { Instant } from './instant.ts'
import { toLocalDateUtc } from './instant.ts'
import type { LocalDate } from './local-date.ts'

// Pinned by hand for the same reason money formatting is: ICU builds differ per host.
const MONTHS: Readonly<Record<Locale, readonly string[]>> = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  hu: [
    'január',
    'február',
    'március',
    'április',
    'május',
    'június',
    'július',
    'augusztus',
    'szeptember',
    'október',
    'november',
    'december',
  ],
}

const SHORT_MONTHS: Readonly<Record<Locale, readonly string[]>> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  hu: ['jan.', 'febr.', 'márc.', 'ápr.', 'máj.', 'jún.', 'júl.', 'aug.', 'szept.', 'okt.', 'nov.', 'dec.'],
}

export type DateStyle = 'iso' | 'short' | 'long'

export function formatDate(value: LocalDate, locale: Locale, style: DateStyle = 'short'): string {
  const { year, month, day } = civilFromDays(value.epochDay)
  if (style === 'iso') {
    return `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`
  }
  const names = style === 'long' ? MONTHS[locale] : SHORT_MONTHS[locale]
  const name = names[month - 1]!
  return locale === 'hu' ? `${year}. ${name} ${day}.` : `${day} ${name} ${year}`
}

/** Month name and year, no day - `March 2026` / `2026. március`. */
export function formatMonthYear(value: LocalDate, locale: Locale): string {
  const { year, month } = civilFromDays(value.epochDay)
  const name = MONTHS[locale][month - 1]!
  return locale === 'hu' ? `${year}. ${name}` : `${name} ${year}`
}

/** Short month name for a chart axis - `Jan` / `okt.`. */
export function formatMonthShort(value: LocalDate, locale: Locale): string {
  const { month } = civilFromDays(value.epochDay)
  return SHORT_MONTHS[locale][month - 1]!
}

/** Renders the instant's own UTC calendar day; never the viewer's local day. */
export function formatInstantDate(value: Instant, locale: Locale, style: DateStyle = 'short'): string {
  return formatDate(toLocalDateUtc(value), locale, style)
}

export function formatInstantTimeUtc(value: Instant): string {
  const secondOfDay = Number(value.epochSecond - BigInt(toLocalDateUtc(value).epochDay) * 86_400n)
  const hh = Math.floor(secondOfDay / 3600)
  const mm = Math.floor((secondOfDay % 3600) / 60)
  return `${pad(hh)}:${pad(mm)} UTC`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}
